import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import GroupSignInPanel from './components/GroupSignInPanel';

import { T } from './theme';
const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:10,
  boxShadow:'0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase', color:T.inkSoft, fontWeight:600 };
const btn = (primary) => ({ borderRadius:8, padding:'8px 14px', font:'inherit', fontWeight:600,
  fontSize:13, cursor:'pointer', background: primary ? T.rose : '#fff',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : `1px solid ${T.b300}` });
const inp = { font:'inherit', fontSize:13, padding:'7px 9px', borderRadius:8,
  border:`1px solid ${T.b300}`, color:T.ink, background:'#fff', boxSizing:'border-box' };
const pill = (bg, fg, text) => <span style={{ background:bg, color:fg, borderRadius:99,
  padding:'2px 9px', fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>{text}</span>;

const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const hhmm = (t) => (t || '').slice(0, 5);
const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = t.slice(0,5).split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2,'0')} ${ap}`;
};
const addDays = (dateStr, n) => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const mondayOf = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay() === 0 ? 7 : d.getDay();
  return addDays(dateStr, -(day - 1));
};
const niceDate = (ds) => new Date(ds + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' });
const dOnly = (v) => String(v || '').slice(0, 10);
const todayPacific = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
const durFromRange = (s, e) => {
  if (!s || !e) return 60;
  const [sh, sm] = s.slice(0,5).split(':').map(Number);
  const [eh, em] = e.slice(0,5).split(':').map(Number);
  const d = (eh * 60 + em) - (sh * 60 + sm);
  return d > 0 ? d : 60;
};
const endFromStart = (s, mins) => {
  if (!s) return '';
  const [sh, sm] = s.slice(0,5).split(':').map(Number);
  const t = sh * 60 + sm + (Number(mins) || 60);
  return `${String(Math.floor(t / 60) % 24).padStart(2,'0')}:${String(t % 60).padStart(2,'0')}`;
};

const EMPTY_TPL = { day_of_week:1, start_time:'09:00', end_time:'12:00', topic:'', description:'', facilitator_id:'', house_id:'', client_ids:[] };

export default function EMRSchedule({ patients, onStartSession, onOpenGroup }) {
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(null);
  const [sessView, setSessView] = useState(null);
  const [weekStart, setWeekStart] = useState(mondayOf(todayPacific()));
  const [week, setWeek] = useState({ instances: [] });
  const [users, setUsers] = useState([]);
  const [showPositions, setShowPositions] = useState(false);
  const [prog, setProg] = useState(''); // program filter: '' = all programs
  const [titleDraft, setTitleDraft] = useState(null); // { id, text } while a group title is being edited
  const [facEligible, setFacEligible] = useState([]);   // this facility's credentialed clinical staff
  const [facBhts, setFacBhts] = useState([]);           // this facility's BHTs (for BHT-run seats)
  const [facNoCred, setFacNoCred] = useState([]);       // clinical staff left off for lack of a credential
  useEffect(() => {
    if (!facilityId) { setFacEligible([]); setFacBhts([]); setFacNoCred([]); return; }
    axios.get(`${API}/api/emr/group-schedule/facilitators`, { params: { facility_id: facilityId } })
      .then(r => { setFacEligible(r.data.facilitators || []); setFacBhts(r.data.bhts || []); setFacNoCred(r.data.no_credential || []); })
      .catch(() => { setFacEligible([]); setFacBhts([]); setFacNoCred([]); });
  }, [facilityId]);
  const [positions, setPositions] = useState([]);
  const [posCanEdit, setPosCanEdit] = useState(false);
  const [autoInfo, setAutoInfo] = useState(null); // automatic clinician assignment: status + who is next in line
  const loadPositions = async (fid) => {
    if (!fid) { setPositions([]); return; }
    try {
      const r = await axios.get(`${API}/api/emr/clinical-positions`, { params: { facility_id: fid } });
      setPositions(r.data.positions || []); setPosCanEdit(!!r.data.can_edit);
      axios.get(`${API}/api/emr/clinical-positions/auto-assign`, { params: { facility_id: fid } }).then(a => setAutoInfo(a.data)).catch(() => setAutoInfo(null));
    } catch (e) { setPositions([]); setPosCanEdit(false); }
  };
  useEffect(() => { loadPositions(facilityId); }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleAuto = async () => {
    if (!autoInfo) return;
    const on = !autoInfo.enabled;
    const msg = on
      ? 'Turn on automatic clinician assignment for this facility?' + (autoInfo.live ? '' : '\n\nIt will not assign anyone until this facility\'s EMR is live.')
      : 'Turn off automatic clinician assignment for this facility?';
    if (!window.confirm(msg)) return;
    try { await axios.post(`${API}/api/emr/clinical-positions/auto-assign`, { facility_id: facilityId, on }); loadPositions(facilityId); }
    catch (e) { alert((e.response && e.response.data && e.response.data.error) || 'Could not change it.'); }
  };
  const assignPosition = async (pos, userId) => {
    try {
      const r = await axios.patch(`${API}/api/emr/clinical-positions/${pos.id}`, { user_id: userId || null });
      await loadPositions(facilityId);
      loadTemplates();
      say('ok', pos.label + (userId ? ' assigned. ' : ' cleared. ') + r.data.groups + ' recurring group' + (r.data.groups === 1 ? '' : 's') + ' updated.');
    } catch (e) { alert((e.response && e.response.data && e.response.data.error) || 'Could not change the position.'); }
  };
  const [houses, setHouses] = useState([]);
  const [sel, setSel] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplForm, setTplForm] = useState(null);
  const [adhocDay, setAdhocDay] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);
  const [search, setSearch] = useState('');
  const [tplSearch, setTplSearch] = useState('');
  const [tplHouseFilter, setTplHouseFilter] = useState('ALL');
  const [dayHouseFilter, setDayHouseFilter] = useState('ALL');

  const say = (kind, text) => { setMsg({ kind, text }); setTimeout(() => setMsg(null), 5000); };
  const errText = (e) => (e.response && e.response.data && e.response.data.error) || e.message;

  const facilityName = (facilities.find(f => f.id === facilityId) || {}).org_name || '';
  const facPatients = patients.filter(p => p.facility_id === facilityId && p.episode_id);
  const facHouseNames = [...new Set(facPatients.map(p => p.house_name).filter(Boolean))].sort();
  const facHouses = houses.filter(h => !h.facility_id || h.facility_id === facilityId);

  useEffect(() => {
    axios.get(`${API}/api/emr/group-schedule/facilities`).then(r => {
      const list = r.data.facilities || [];
      setFacilities(list);
      setFacilityId(prev => (prev && list.some(f => f.id === prev)) ? prev : (list.length ? list[0].id : null));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/staff-list`).then(r => {
      const body = r.data || {};
      const list = Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : (body.users || []));
      const act = list.filter(u => u.active !== false);
      const rank = (u) => (u.role === 'clinical' ? 0 : ((u.role === 'ceo' || u.role === 'admin') ? 1 : 2));
      act.sort((a, b) => rank(a) - rank(b) || (a.last_name || '').localeCompare(b.last_name || ''));
      setUsers(act);
    }).catch(() => {});
    axios.get(`${API}/api/emr/houses`).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data.houses || []);
      setHouses(list);
    }).catch(() => {});
  }, []);

  const loadWeek = useCallback(async () => {
    if (!facilityId) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/emr/group-schedule/week`,
        { params: { facility_id: facilityId, week_start: weekStart } });
      setWeek(r.data);
    } catch (e) { say('err', 'Load failed: ' + errText(e)); }
    setBusy(false);
  }, [facilityId, weekStart]); // eslint-disable-line

  useEffect(() => { loadWeek(); }, [loadWeek]);

  const loadTemplates = async () => {
    if (!facilityId) return;
    try {
      const r = await axios.get(`${API}/api/emr/group-schedule/templates`, { params: { facility_id: facilityId } });
      setTemplates(r.data.templates || []);
    } catch (e) { say('err', errText(e)); }
  };
  useEffect(() => { if (showTemplates) loadTemplates(); }, [showTemplates, facilityId]); // eslint-disable-line

  const saveTemplate = async () => {
    if (!tplForm || !tplForm.topic.trim()) { say('err', 'Topic required'); return; }
    setBusy(true);
    try {
      const body = { ...tplForm, facility_id: facilityId,
        facilitator_id: tplForm.facilitator_id || null, house_id: tplForm.house_id || null, program: tplForm.program || null,
        duration_minutes: durFromRange(tplForm.start_time, tplForm.end_time) };
      let tid = tplForm.id;
      if (tid) await axios.put(`${API}/api/emr/group-schedule/templates/${tid}`, body);
      else { const cr = await axios.post(`${API}/api/emr/group-schedule/templates`, body); tid = cr.data.template.id; }
      await axios.post(`${API}/api/emr/group-schedule/templates/${tid}/roster`, {
        patients: (tplForm.client_ids || []).map(cid => {
          const pp = facPatients.find(x => x.id === cid);
          return { patient_id: cid, episode_id: pp ? pp.episode_id : null };
        }) });
      say('ok', tplForm.id ? 'Template updated (applies to future weeks).' : 'Template created.');
      setTplForm(null); loadTemplates(); loadWeek();
    } catch (e) { say('err', errText(e)); }
    setBusy(false);
  };

  const deactivateTemplate = async (id) => {
    if (!window.confirm('Deactivate this recurring group? Existing weeks keep their instances.')) return;
    try { await axios.post(`${API}/api/emr/group-schedule/templates/${id}/deactivate`); loadTemplates(); say('ok','Template deactivated.'); }
    catch (e) { say('err', errText(e)); }
  };

  const createAdhoc = async (date, form) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/emr/group-schedule/instances`, {
        facility_id: facilityId, session_date: date, start_time: form.start_time,
        duration_minutes: durFromRange(form.start_time, form.end_time), topic: form.topic,
        description: form.description || null, program: prog || null, facilitator_id: form.facilitator_id || null,
        house_id: form.house_id || null });
      say('ok', 'Group added to ' + niceDate(date) + '.');
      setAdhocDay(null); loadWeek();
    } catch (e) { say('err', errText(e)); }
    setBusy(false);
  };

  const updateInstance = async (id, patch) => {
    setBusy(true);
    try { await axios.put(`${API}/api/emr/group-schedule/instances/${id}`, patch); loadWeek(); }
    catch (e) { say('err', errText(e)); }
    setBusy(false);
  };
  const renameGroup = async (inst, title, everyWeek) => {
    setBusy(true);
    try {
      if (everyWeek && inst.template_id) await axios.put(`${API}/api/emr/group-schedule/templates/${inst.template_id}`, { topic: title });
      await axios.put(`${API}/api/emr/group-schedule/instances/${inst.id}`, { topic: title });
      setTitleDraft(null);
      await loadWeek();
      loadTemplates();
      say('ok', everyWeek ? 'Renamed for every week.' : 'Renamed for this day.');
    } catch (e) { say('err', errText(e)); }
    setBusy(false);
  };
  const cancelInstance = async (id) => {
    const reason = window.prompt('Reason for cancelling (optional):') || null;
    try { await axios.post(`${API}/api/emr/group-schedule/instances/${id}/cancel`, { reason }); loadWeek(); }
    catch (e) { say('err', errText(e)); }
  };
  const uncancelInstance = async (id) => {
    try { await axios.post(`${API}/api/emr/group-schedule/instances/${id}/uncancel`); loadWeek(); }
    catch (e) { say('err', errText(e)); }
  };

  const addToRoster = async (instId, list) => {
    try {
      await axios.post(`${API}/api/emr/group-schedule/instances/${instId}/roster`,
        { patients: list.map(p => ({ patient_id: p.id, episode_id: p.episode_id })) });
      loadWeek();
    } catch (e) { say('err', errText(e)); }
  };
  const removeFromRoster = async (instId, patientId) => {
    try {
      await axios.post(`${API}/api/emr/group-schedule/instances/${instId}/roster/remove`, { patient_id: patientId });
      loadWeek();
    } catch (e) { say('err', errText(e)); }
  };

  const openSession = async (instId) => {
    try {
      const r = await axios.get(`${API}/api/emr/group-schedule/instances/${instId}/session`);
      setSessView(r.data);
    } catch (e) { say('err', errText(e)); }
  };
  const startSession = async (inst) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/emr/group-schedule/instances/${inst.id}/start`);
      await loadWeek();
      if (r.data.mode === 'pick') {
        // Pick-mode facility: hand off to the Groups tab with nobody checked; staff pick attendees.
        if (onStartSession) onStartSession({
          instanceId: inst.id, facility_id: inst.facility_id, house_name: inst.house_name || 'ALL',
          topic: inst.topic || '', curriculum: inst.description || '',
          date: dOnly(inst.session_date), startTime: hhmm(inst.start_time),
          duration: inst.duration_minutes || 60, patientIds: [] });
        else openSession(inst.id);
      } else {
        say('ok', `Session started — ${(r.data.notes || []).length} notes created.`);
        if (onOpenGroup) onOpenGroup(inst.id); else openSession(inst.id);
      }
    } catch (e) { say('err', errText(e)); }
    setBusy(false);
  };
  const endSession = async (instId) => {
    try { await axios.post(`${API}/api/emr/group-schedule/instances/${instId}/end`); loadWeek(); openSession(instId); }
    catch (e) { say('err', errText(e)); }
  };
  const autoTried = useRef({});
  useEffect(() => {
    const t = todayPacific();
    const nowHM = new Date().toLocaleTimeString('en-GB', { timeZone:'America/Los_Angeles', hour:'2-digit', minute:'2-digit' });
    (week.instances || []).forEach(i => {
      const isToday = dOnly(i.session_date) === t;
      const endHM = endFromStart(hhmm(i.start_time), i.duration_minutes);
      if (i.status === 'scheduled' && i.group_start_mode === 'roster' && !autoTried.current[i.id] && isToday
          && hhmm(i.start_time) <= nowHM && endHM > nowHM && (i.roster || []).length) {
        autoTried.current[i.id] = true;
        axios.post(`${API}/api/emr/group-schedule/instances/${i.id}/start`).then(() => loadWeek()).catch(() => {});
      }
      if (i.status === 'in_session' && !autoTried.current['end_' + i.id]
          && (!isToday && dOnly(i.session_date) < t || (isToday && endHM <= nowHM))) {
        autoTried.current['end_' + i.id] = true;
        axios.post(`${API}/api/emr/group-schedule/instances/${i.id}/end`).then(() => loadWeek()).catch(() => {});
      }
    });
  }, [week]); // eslint-disable-line

  const byDay = {};
  (week.instances || []).filter(i => !prog || i.program === prog).forEach(i => { const k = dOnly(i.session_date); (byDay[k] = byDay[k] || []).push(i); });
  const selected = (week.instances || []).find(i => i.id === sel) || null;
  const today = todayPacific();

  const facPrograms = ((facilities.find(f => f.id === facilityId) || {}).group_programs) || [];
  // Facilitator pickers list only this facility's credentialed clinical staff (BHT seats list its BHTs).
  // Whoever is already assigned stays visible even when they are not on the list, so nothing blanks out.
  const pickList = (currentId, bhtSeat) => {
    const base = bhtSeat ? facBhts : facEligible;
    if (!currentId || base.some(u => u.id === currentId)) return base;
    const cur = users.find(u => u.id === currentId);
    return cur ? base.concat([{ ...cur, credentials: null }]) : base;
  };
  const pickLabel = (u) => u.first_name + ' ' + u.last_name + (u.credentials ? ', ' + u.credentials : (u.role ? ' (' + u.role + ')' : ''));
  const facilitatorSelect = (value, onChange) => (
    <select style={inp} value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">— Facilitator —</option>
      {pickList(value, false).map(u => (
        <option key={u.id} value={u.id}>{pickLabel(u)}</option>
      ))}
    </select>
  );

  const tplBuilder = () => (
    <div style={{ ...card, padding:14, marginBottom:12, background:T.b100 }}>
      <div style={{ ...label, marginBottom:8 }}>{tplForm.id ? 'Edit Recurring Group' : 'New Recurring Group'}</div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
        <select style={inp} value={tplForm.day_of_week} onChange={e => setTplForm(f => ({ ...f, day_of_week:Number(e.target.value) }))}>
          {DAYS.map((d, i) => <option key={d} value={i+1}>{d}</option>)}
        </select>
        <input style={{ ...inp, width:110 }} type="time" value={tplForm.start_time}
          onChange={e => setTplForm(f => ({ ...f, start_time:e.target.value }))} />
        <span style={{ fontSize:12, color:T.inkSoft }}>to</span>
        <input style={{ ...inp, width:110 }} type="time" value={tplForm.end_time}
          onChange={e => setTplForm(f => ({ ...f, end_time:e.target.value }))} />
        {facilitatorSelect(tplForm.facilitator_id, v => setTplForm(f => ({ ...f, facilitator_id:v })))}
        <select style={inp} value={tplForm.house_id || ''} onChange={e => setTplForm(f => ({ ...f, house_id:e.target.value }))}>
          <option value="">All houses</option>
          {facHouses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        {facPrograms.length > 0 && (
          <select style={inp} value={tplForm.program || ''} onChange={ev => setTplForm(f => ({ ...f, program:ev.target.value }))}>
            <option value="">Program</option>
            {facPrograms.map(gp => <option key={gp.key} value={gp.key}>{gp.label}</option>)}
          </select>
        )}
      </div>
      <div style={{ ...label, marginTop:10 }}>Title</div>
      <input style={{ ...inp, width:'100%', marginTop:4 }} placeholder="Group title (e.g. Relapse Prevention)"
        value={tplForm.topic} onChange={e => setTplForm(f => ({ ...f, topic:e.target.value }))} />
      <div style={{ ...label, marginTop:10 }}>Description</div>
      <textarea style={{ ...inp, width:'100%', marginTop:4, minHeight:50, resize:'vertical' }}
        placeholder="Description / curriculum notes"
        value={tplForm.description} onChange={e => setTplForm(f => ({ ...f, description:e.target.value }))} />
      <div style={{ marginTop:10 }}>
        <div style={{ ...label, marginBottom:6 }}>Clients in this group ({(tplForm.client_ids || []).length}) — every week starts with this roster; day-of moves happen on the week grid</div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:6 }}>
          {facHouseNames.map(hn => (
            <button key={hn} style={{ ...btn(false), fontSize:12, padding:'3px 10px' }}
              onClick={() => setTplForm(f => ({ ...f, client_ids:[...new Set([...(f.client_ids||[]), ...facPatients.filter(p => p.house_name === hn).map(p => p.id)])] }))}>
              + All {hn}</button>
          ))}
          <button style={{ ...btn(false), fontSize:12, padding:'3px 10px' }}
            onClick={() => setTplForm(f => ({ ...f, client_ids: facPatients.map(p => p.id) }))}>+ Entire Census</button>
          <button style={{ ...btn(false), fontSize:12, padding:'3px 10px' }}
            onClick={() => setTplForm(f => ({ ...f, client_ids: [] }))}>Clear</button>
        </div>
        <div style={{ display:'flex', gap:6, marginBottom:6 }}>
          <select style={inp} value={tplHouseFilter} onChange={e => setTplHouseFilter(e.target.value)}>
            <option value="ALL">All houses</option>
            {facHouseNames.map(hn => <option key={hn} value={hn}>{hn}</option>)}
          </select>
          <input style={{ ...inp, flex:1 }} placeholder="Search clients…"
            value={tplSearch} onChange={e => setTplSearch(e.target.value)} />
        </div>
        <div style={{ maxHeight:170, overflowY:'auto', background:'#fff', borderRadius:8, padding:'4px 8px' }}>
          {facPatients
            .filter(p => tplHouseFilter === 'ALL' || p.house_name === tplHouseFilter)
            .filter(p => !tplSearch || `${p.first_name} ${p.last_name}`.toLowerCase().includes(tplSearch.toLowerCase()))
            .map(p => {
              const on = (tplForm.client_ids || []).includes(p.id);
              return (
                <label key={p.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'3px 0', fontSize:13, cursor:'pointer' }}>
                  <input type="checkbox" checked={on}
                    onChange={() => setTplForm(f => ({ ...f,
                      client_ids: on ? (f.client_ids||[]).filter(x => x !== p.id) : [...(f.client_ids||[]), p.id] }))} />
                  <span>{p.first_name} {p.last_name}</span>
                  <span style={{ color:T.inkSoft, fontSize:11.5 }}>{p.house_name || ''}</span>
                </label>
              );
            })}
        </div>
      </div>
      <div style={{ display:'flex', gap:8, marginTop:10 }}>
        <button style={btn(true)} disabled={busy} onClick={saveTemplate}>{tplForm.id ? 'Save Changes' : 'Create'}</button>
        <button style={btn(false)} onClick={() => setTplForm(null)}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div>
      {msg && (
        <div style={{ ...card, padding:'9px 13px', marginBottom:10, fontSize:13, fontWeight:600,
          background: msg.kind === 'ok' ? T.passBg : T.blockingBg,
          color: msg.kind === 'ok' ? T.pass : T.blocking }}>{msg.text}</div>
      )}

      {sessView && (
        <div style={{ ...card, padding:16, marginBottom:14, borderLeft:`4px solid ${T.rose}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <strong style={{ fontSize:15 }}>{sessView.instance.topic}</strong>
            <span style={{ fontSize:13, color:T.inkSoft }}>
              {niceDate(dOnly(sessView.instance.session_date))} · {fmt12(sessView.instance.start_time)} · {sessView.instance.facilitator_name || 'No facilitator'}
            </span>
            {sessView.instance.status === 'in_session' && pill(T.warningBg, T.warning, '● In Session')}
            {sessView.instance.status === 'completed' && pill(T.passBg, T.pass, 'Completed')}
            <span style={{ flex:1 }} />
            <button style={btn(false)} onClick={() => setSessView(null)}>Close</button>
          </div>
          <div style={{ ...label, marginTop:12, marginBottom:6 }}>Clients & Notes ({(sessView.clients || []).length})</div>
          {(sessView.clients || []).map(c => {
            const n = c.note;
            const st = n ? n.status : null;
            const bg = !n ? T.b100 : st === 'finalized' || st === 'amended' ? T.passBg : st === 'kicked_back' ? T.blockingBg : st === 'in_review' || st === 'ai_drafted' ? T.infoBg : T.warningBg;
            const fg = !n ? T.inkSoft : st === 'finalized' || st === 'amended' ? T.pass : st === 'kicked_back' ? T.blocking : st === 'in_review' || st === 'ai_drafted' ? T.info : T.warning;
            const txt = !n ? 'No note' : (st || '').replace(/_/g, ' ');
            return (
              <div key={c.patient_id} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderTop:`1px solid ${T.b100}`, fontSize:13 }}>
                <span style={{ fontWeight:600, minWidth:160 }}>{c.patient_name}</span>
                {pill(bg, fg, txt)}
                {n && n.attended === 'no' && pill(T.b100, T.inkSoft, 'Absent')}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:12, flexWrap:'wrap' }}>
        <select style={inp} value={facilityId || ''} onChange={e => { setFacilityId(e.target.value); setSel(null); setSessView(null); setProg(''); }}>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}{f.is_sandbox ? ' (sandbox)' : ''}</option>)}
        </select>
        {facPrograms.length > 1 && (
          <select style={inp} value={prog} onChange={ev => setProg(ev.target.value)}>
            <option value="">All programs</option>
            {facPrograms.map(gp => <option key={gp.key} value={gp.key}>{gp.label}</option>)}
          </select>
        )}
        <button style={btn(false)} onClick={() => setWeekStart(addDays(weekStart, -7))}>← Prev</button>
        <strong style={{ fontSize:14, color:T.ink }}>Week of {niceDate(weekStart)} – {niceDate(addDays(weekStart, 6))}</strong>
        <button style={btn(false)} onClick={() => setWeekStart(addDays(weekStart, 7))}>Next →</button>
        <button style={btn(false)} onClick={() => setWeekStart(mondayOf(today))}>Today</button>
        <span style={{ flex:1 }} />
        <button style={btn(showPositions)} onClick={() => setShowPositions(s => !s)}>Clinical Positions</button>
        <button style={btn(showTemplates)} onClick={() => setShowTemplates(s => !s)}>Recurring Groups</button>
      </div>

      {showPositions && (
        <div style={{ ...card, padding:14, marginBottom:14 }}>
          <div style={label}>Clinical Positions</div>
          <div style={{ fontSize:12.5, color:T.inkSoft, margin:'4px 0 10px' }}>Put a clinician in each position. Every recurring group that position runs picks them up, along with upcoming sessions that have not started. Move someone to a different position and their groups follow.</div>
          {positions.length === 0 && <div style={{ fontSize:13, color:T.inkSoft }}>No positions are set up for this facility yet.</div>}
          {facNoCred.length > 0 && <div style={{ fontSize:12, color:'#B45309', marginBottom:8 }}>{'Not in the pickers (no credential on file): ' + facNoCred.join(', ') + '. Add their credential under Users.'}</div>}
          {[...new Set(positions.map(x => x.program))].filter(pk => !prog || pk === prog).map(pk => (
            <div key={pk} style={{ marginBottom:10 }}>
              <div style={{ fontSize:12.5, fontWeight:600, color:T.ink, margin:'8px 0 2px' }}>{((facPrograms.find(gp => gp.key === pk) || {}).label ? (facPrograms.find(gp => gp.key === pk) || {}).label + ' (' + pk + ')' : pk)}</div>
              {positions.filter(x => x.program === pk).map(x => (
                <div key={x.id} style={{ display:'flex', gap:10, alignItems:'center', padding:'7px 0', borderTop:`1px solid ${T.b100}`, fontSize:13, flexWrap:'wrap' }}>
                  <span style={{ fontWeight:600, minWidth:200 }}>{x.label}</span>
                  {posCanEdit ? (
                    <select style={inp} value={x.user_id || ''} onChange={e => assignPosition(x, e.target.value)}>
                      <option value="">Unassigned</option>
                      {pickList(x.user_id, x.discipline === 'bht').map(u => <option key={u.id} value={u.id}>{pickLabel(u)}</option>)}
                    </select>
                  ) : <span>{x.user_name || 'Unassigned'}</span>}
                  <span style={{ color:T.inkSoft }}>{x.group_count + ' group' + (x.group_count === 1 ? '' : 's') + ' a week'}</span>
                  {x.caseload_cap != null && <span style={{ fontWeight:600, color: x.caseload > x.caseload_cap ? '#E5484D' : T.ink }}>{'Caseload ' + x.caseload + ' / ' + x.caseload_cap}</span>}
                  {x.notes && <span style={{ color:T.inkSoft }}>{x.notes}</span>}
                </div>
              ))}
            </div>
          ))}
          {autoInfo && (
            <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${T.b100}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                <div style={label}>Automatic assignment</div>
                <span style={{ fontSize:12.5, fontWeight:600, color: autoInfo.running ? '#1F9D55' : '#B45309' }}>{autoInfo.status_text}</span>
                <span style={{ flex:1 }} />
                {posCanEdit && <button style={btn(false)} onClick={toggleAuto}>{autoInfo.enabled ? 'Turn off' : 'Turn on'}</button>}
              </div>
              <div style={{ fontSize:12.5, color:T.inkSoft, margin:'4px 0 8px' }}>New admits, and clients who move between programs, get the eligible clinician with the fewest clients in that program. Anyone who can edit the bed board can change the assignment afterward.</div>
              {(autoInfo.programs || []).filter(pg => !prog || pg.program === prog).map(pg => (
                <div key={pg.program} style={{ fontSize:13, padding:'4px 0' }}>
                  <div style={{ fontWeight:600 }}>{pg.label}</div>
                  {(pg.lines || []).map((ln, i) => <div key={i} style={{ color:T.ink, paddingLeft:10 }}>{ln}</div>)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showTemplates && (
        <div style={{ ...card, padding:14, marginBottom:14 }}>
          <div style={{ display:'flex', alignItems:'center', marginBottom:10 }}>
            <div style={label}>Recurring Weekly Program</div>
            <span style={{ flex:1 }} />
            {!tplForm && <button style={btn(true)} onClick={() => setTplForm({ ...EMPTY_TPL, program: prog || '' })}>+ New Recurring Group</button>}
          </div>
          {tplForm && tplBuilder()}
          {templates.length === 0 && !tplForm && (
            <div style={{ fontSize:13, color:T.inkSoft }}>No recurring groups yet — build the weekly program once and every week fills itself in.</div>
          )}
          {templates.filter(t => !prog || !('program' in t) || t.program === prog).map(t => (
            <div key={t.id} style={{ display:'flex', gap:10, alignItems:'center', padding:'8px 0',
              borderTop:`1px solid ${T.b100}`, fontSize:13 }}>
              <span style={{ fontWeight:600, width:38 }}>{DAYS[t.day_of_week-1]}</span>
              <span style={{ width:76 }}>{fmt12(t.start_time)}</span>
              <span style={{ fontWeight:600, color:T.ink }}>{t.topic}</span>
              <span style={{ color:T.inkSoft }}>{t.facilitator_name || 'No facilitator'} · {t.house_name || 'All houses'} · {t.duration_minutes}m · {(t.roster || []).length} clients</span>
              <span style={{ flex:1 }} />
              <button style={btn(false)} onClick={() => setTplForm({ id:t.id, day_of_week:t.day_of_week,
                start_time:hhmm(t.start_time), duration_minutes:t.duration_minutes, topic:t.topic,
                end_time:endFromStart(hhmm(t.start_time), t.duration_minutes), description:t.description || '', program:t.program || '', facilitator_id:t.facilitator_id || '', house_id:t.house_id || '', client_ids:(t.roster || []).map(r => r.patient_id) })}>Edit</button>
              <button style={btn(false)} onClick={() => deactivateTemplate(t.id)}>Deactivate</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:8 }}>
        {DAYS.map((d, i) => {
          const date = addDays(weekStart, i);
          const items = (byDay[date] || []).slice().sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
          return (
            <div key={d} style={{ ...card, padding:8, minHeight:120,
              border: date === today ? `2px solid ${T.rose}` : card.border }}>
              <div style={{ ...label, marginBottom:6 }}>{d} {niceDate(date)}</div>
              {items.map(inst => (
                <div key={inst.id} onClick={() => setSel(inst.id === sel ? null : inst.id)}
                  style={{ borderRadius:8, padding:'7px 8px', marginBottom:6, cursor:'pointer',
                    background: inst.status === 'cancelled' ? T.b100 : inst.status === 'completed' ? T.passBg : inst.status === 'in_session' ? T.warningBg : T.infoBg,
                    opacity: inst.status === 'cancelled' ? .6 : 1,
                    outline: sel === inst.id ? `2px solid ${T.rose}` : 'none' }}>
                  <div style={{ fontSize:12, fontWeight:600, color:T.ink }}>{fmt12(inst.start_time)}–{fmt12(endFromStart(hhmm(inst.start_time), inst.duration_minutes))} · {inst.topic}</div>
                  <div style={{ fontSize:11, color:T.inkSoft }}>
                    {inst.facilitator_name || 'No facilitator'} · {(inst.roster || []).length} clients
                    {inst.status === 'cancelled' ? ' · CANCELLED' : ''}{inst.status === 'completed' ? ' · ✓ Done' : ''}{inst.status === 'in_session' ? ' · ● LIVE' : ''}
                  </div>
                </div>
              ))}
              {adhocDay === date ? (
                <AdhocForm onCreate={(f) => createAdhoc(date, f)} onCancel={() => setAdhocDay(null)}
                  users={users} houses={facHouses} />
              ) : (
                <button style={{ ...btn(false), width:'100%', padding:'5px 0', fontSize:12 }}
                  onClick={() => setAdhocDay(date)}>+ Add group</button>
              )}
            </div>
          );
        })}
      </div>

      {selected && (
        <div style={{ ...card, padding:16, marginTop:14 }}>
          <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            {selected.status === 'scheduled' && pill(T.infoBg, T.info, 'Scheduled')}
            {selected.status === 'cancelled' && pill(T.b100, T.inkSoft, 'Cancelled')}
            {selected.status === 'completed' && pill(T.passBg, T.pass, 'Completed')}
            {selected.status === 'in_session' && pill(T.warningBg, T.warning, '● In Session')}
            <strong style={{ fontSize:15 }}>{selected.topic}</strong>
            <span style={{ fontSize:13, color:T.inkSoft }}>
              {niceDate(dOnly(selected.session_date))} · {fmt12(selected.start_time)}–{fmt12(endFromStart(hhmm(selected.start_time), selected.duration_minutes))} ({selected.duration_minutes}m)
              {selected.house_name ? ` · ${selected.house_name}` : ''}
            </span>
            <span style={{ flex:1 }} />
            {selected.status === 'scheduled' && (
              <>
                <button style={btn(true)} onClick={() => startSession(selected)}>Start Session →</button>
                <button style={btn(false)} onClick={() => cancelInstance(selected.id)}>Cancel Group</button>
              </>
            )}
            {selected.status === 'cancelled' && (
              <button style={btn(false)} onClick={() => uncancelInstance(selected.id)}>Restore</button>
            )}
            {selected.status === 'in_session' && (
              <>
                <button style={btn(true)} onClick={() => onOpenGroup && onOpenGroup(selected.id)}>Open Group Note →</button>
                <button style={btn(false)} onClick={() => endSession(selected.id)}>End Group</button>
              </>
            )}
            {selected.status === 'completed' && (
              <button style={btn(true)} onClick={() => onOpenGroup && onOpenGroup(selected.id)}>Open Group Note →</button>
            )}
          </div>
          {selected.cancel_reason && (
            <div style={{ fontSize:12.5, color:T.inkSoft, marginTop:6 }}>Cancelled: {selected.cancel_reason}</div>
          )}

          {selected.status === 'scheduled' && (
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginTop:12 }}>
              <span style={label}>Quick edit</span>
              <input style={{ ...inp, width:110 }} type="time" defaultValue={hhmm(selected.start_time)}
                onBlur={e => e.target.value !== hhmm(selected.start_time) && updateInstance(selected.id, { start_time: e.target.value, duration_minutes: durFromRange(e.target.value, endFromStart(e.target.value, selected.duration_minutes)) })} />
              <span style={{ fontSize:12, color:T.inkSoft }}>to</span>
              <input style={{ ...inp, width:110 }} type="time" defaultValue={endFromStart(hhmm(selected.start_time), selected.duration_minutes)}
                onBlur={e => updateInstance(selected.id, { duration_minutes: durFromRange(hhmm(selected.start_time), e.target.value) })} />
              {facilitatorSelect(selected.facilitator_id, v => updateInstance(selected.id, { facilitator_id: v || null }))}
              <span style={label}>Title</span>
              <input style={{ ...inp, flex:1, minWidth:180 }} value={titleDraft && titleDraft.id === selected.id ? titleDraft.text : (selected.topic || '')}
                onChange={e => setTitleDraft({ id: selected.id, text: e.target.value })} />
              {titleDraft && titleDraft.id === selected.id && titleDraft.text.trim() && titleDraft.text.trim() !== (selected.topic || '') && (
                <>
                  <button style={btn(true)} disabled={busy} onClick={() => renameGroup(selected, titleDraft.text.trim(), false)}>Save for this day</button>
                  {selected.template_id && <button style={btn(false)} disabled={busy} onClick={() => renameGroup(selected, titleDraft.text.trim(), true)}>Rename every week</button>}
                </>
              )}
            </div>
          )}

          <GroupSignInPanel instance={selected} onChanged={loadWeek} />
          <div style={{ marginTop:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <div style={label}>Clients ({(selected.roster || []).length})</div>
              {selected.status === 'scheduled' && (
                <button style={btn(pickerFor === selected.id)} onClick={() => { setPickerFor(pickerFor === selected.id ? null : selected.id); setSearch(''); }}>
                  {pickerFor === selected.id ? 'Done' : '+ Add / Remove Clients'}
                </button>
              )}
            </div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {(selected.roster || []).map(r => (
                <span key={r.id} style={{ background:T.b100, borderRadius:99, padding:'4px 10px', fontSize:12.5 }}>
                  {r.patient_name}
                  {selected.status === 'scheduled' && (
                    <span style={{ marginLeft:6, cursor:'pointer', color:T.blocking, fontWeight:600 }}
                      onClick={() => removeFromRoster(selected.id, r.patient_id)}>✕</span>
                  )}
                </span>
              ))}
              {(selected.roster || []).length === 0 && <span style={{ fontSize:13, color:T.inkSoft }}>No clients yet.</span>}
            </div>

            {pickerFor === selected.id && (
              <div style={{ marginTop:10, background:T.b100, borderRadius:8, padding:10 }}>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                  {facHouseNames.map(hn => (
                    <button key={hn} style={{ ...btn(false), fontSize:12, padding:'4px 10px' }}
                      onClick={() => addToRoster(selected.id,
                        facPatients.filter(p => p.house_name === hn &&
                          !(selected.roster || []).some(r => r.patient_id === p.id)))}>
                      + All {hn}
                    </button>
                  ))}
                  <button style={{ ...btn(false), fontSize:12, padding:'4px 10px' }}
                    onClick={() => addToRoster(selected.id,
                      facPatients.filter(p => !(selected.roster || []).some(r => r.patient_id === p.id)))}>
                    + Entire Census
                  </button>
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <select style={inp} value={dayHouseFilter} onChange={e => setDayHouseFilter(e.target.value)}>
                    <option value="ALL">All houses</option>
                    {facHouseNames.map(hn => <option key={hn} value={hn}>{hn}</option>)}
                  </select>
                  <input style={{ ...inp, flex:1 }} placeholder="Search clients…"
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div style={{ maxHeight:180, overflowY:'auto', marginTop:6 }}>
                  {facPatients
                    .filter(p => !(selected.roster || []).some(r => r.patient_id === p.id))
                    .filter(p => dayHouseFilter === 'ALL' || p.house_name === dayHouseFilter)
                    .filter(p => !search || `${p.first_name} ${p.last_name}`.toLowerCase().includes(search.toLowerCase()))
                    .map(p => (
                      <div key={p.id} style={{ display:'flex', alignItems:'center', padding:'4px 2px', fontSize:13 }}>
                        <span>{p.first_name} {p.last_name}</span>
                        <span style={{ color:T.inkSoft, marginLeft:8, fontSize:11.5 }}>{p.house_name || ''}</span>
                        <span style={{ flex:1 }} />
                        <button style={{ ...btn(true), padding:'2px 10px', fontSize:12 }}
                          onClick={() => addToRoster(selected.id, [p])}>Add</button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AdhocForm({ onCreate, onCancel, users, houses }) {
  const [f, setF] = useState({ start_time:'13:00', end_time:'16:00', topic:'', description:'', facilitator_id:'', house_id:'' });
  return (
    <div style={{ background:'#fff', border:`1px solid ${T.b300}`, borderRadius:8, padding:8, marginTop:4 }}>
      <input style={{ ...inp, width:'100%', marginBottom:6 }} placeholder="Topic" value={f.topic}
        onChange={e => setF(s => ({ ...s, topic:e.target.value }))} />
      <div style={{ display:'flex', gap:6, marginBottom:6 }}>
        <input style={{ ...inp, flex:1 }} type="time" value={f.start_time}
          onChange={e => setF(s => ({ ...s, start_time:e.target.value }))} />
        <input style={{ ...inp, flex:1 }} type="time" value={f.end_time}
          onChange={e => setF(s => ({ ...s, end_time:e.target.value }))} />
      </div>
      <select style={{ ...inp, width:'100%', marginBottom:6 }} value={f.facilitator_id}
        onChange={e => setF(s => ({ ...s, facilitator_id:e.target.value }))}>
        <option value="">— Facilitator —</option>
        {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
      </select>
      <div style={{ display:'flex', gap:6 }}>
        <button style={{ ...btn(true), flex:1, fontSize:12, padding:'5px 0' }}
          disabled={!f.topic.trim()} onClick={() => onCreate(f)}>Add</button>
        <button style={{ ...btn(false), flex:1, fontSize:12, padding:'5px 0' }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
